import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TASK_STATUS_LABEL,
  formatTaskKey,
  type OverviewSummary,
  type TaskStatus,
  type DepartmentSummary,
  type ProjectSummary,
  type TaskSummary,
  type UserSummary,
  type WorkloadRow,
} from '@todo/shared';
import { ApiError } from '../api/client';
import { overviewApi } from '../api/overview.api';
import { taskApi } from '../api/task.api';
import { departmentApi } from '../api/department.api';
import { projectApi } from '../api/project.api';
import { userApi } from '../api/user.api';
import { STATUS_TONE, dueInfo } from '../lib/task-ui';
import { SidePanel } from '../components/SidePanel';
import { TaskDetailPanel } from '../components/TaskDetailPanel';
import { MultiSelect } from '../components/MultiSelect';

/**
 * The admin dashboard, rebuilt to FIT ONE SCREEN on desktop: the page
 * itself never scrolls (xl and up) - the only scrollbar lives INSIDE the
 * workload table when the team outgrows it. Below xl the page scrolls
 * normally, since nothing fits a phone unscrolled.
 *
 * Every number comes from GET /api/overview; tiles and actions deep-link
 * into Tasks with the matching filter preselected.
 */

type Period = 7 | 30 | 90;

const STATUS_DOT: Record<TaskStatus, string> = {
  DRAFT: 'bg-slate-300',
  PENDING: 'bg-slate-400',
  ONGOING: 'bg-amber-500',
  BLOCKED: 'bg-red-500',
  DONE: 'bg-emerald-500',
  CANCELLED: 'bg-slate-300',
};

// Draft is intentionally absent - tasks always publish immediately now,
// so the breakdown never grows a segment for it, even for a legacy row.
const STATUS_ORDER: TaskStatus[] = ['PENDING', 'ONGOING', 'BLOCKED', 'DONE', 'CANCELLED'];

function Card({ className = '', children }: { className?: string; children: React.ReactNode }) {
  return (
    <section className={`rounded-xl border border-slate-200 bg-white ${className}`}>
      {children}
    </section>
  );
}

/** Tiny glyphs for the KPI tiles - one per tile, in a tinted circle. */
const KPI_ICON_PATH = {
  open: 'M4 6h12M4 10h12M4 14h8',
  overdue: 'M10 5.5V10l3 2M10 2.5a7.5 7.5 0 100 15 7.5 7.5 0 000-15z',
  unassigned: 'M8.5 9a3 3 0 100-6 3 3 0 000 6zM2.5 17a6 6 0 019.5-4.9M13.5 12.5l4 4m0-4l-4 4',
  completed: 'M4 10.5l4 4 8-9',
} as const;

function KpiIcon({ kind }: { kind: keyof typeof KPI_ICON_PATH }) {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-3.5 w-3.5">
      <path d={KPI_ICON_PATH[kind]} stroke="currentColor" strokeWidth="1.6"
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function OverviewPage() {
  const navigate = useNavigate();
  const [days, setDays] = useState<Period>(7);
  const [data, setData] = useState<OverviewSummary | null>(null);
  /** The filter bar: narrow every task number to ANY of the picked
   *  departments / people (multi-select). */
  const [filterDepts, setFilterDepts] = useState<string[]>([]);
  const [filterUsers, setFilterUsers] = useState<string[]>([]);
  const [filterProjects, setFilterProjects] = useState<string[]>([]);
  const [departments, setDepartments] = useState<DepartmentSummary[]>([]);
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  /** The person whose plate is being inspected in the drawer. */
  const [person, setPerson] = useState<WorkloadRow | null>(null);
  const [personTasks, setPersonTasks] = useState<TaskSummary[] | null>(null);
  const [viewing, setViewing] = useState<TaskSummary | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await overviewApi.get(days, {
        ...(filterDepts.length > 0 ? { departmentIds: filterDepts.join(',') } : {}),
        ...(filterUsers.length > 0 ? { userIds: filterUsers.join(',') } : {}),
        ...(filterProjects.length > 0 ? { projectIds: filterProjects.join(',') } : {}),
      }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the dashboard');
    }
  }, [days, filterDepts, filterUsers, filterProjects]);

  useEffect(() => { void load(); }, [load]);

  // The filter options load once - they are the org's full sets regardless
  // of what is currently filtered.
  useEffect(() => {
    void departmentApi.list().then(setDepartments).catch(() => undefined);
    void userApi.list({ pageSize: 100 }).then((res) => setUsers(res.data)).catch(() => undefined);
    void projectApi.list().then(setProjects).catch(() => undefined);
  }, []);

  /** Row click -> drawer with everything on that person's plate. */
  function inspect(row: WorkloadRow) {
    setPerson(row);
    setPersonTasks(null);
    void taskApi
      .list({ assigneeId: row.userId, pageSize: 100, sort: 'dueAt', order: 'asc' })
      .then((res) => {
        // Open work first, completed at the bottom - the question being
        // answered is "what is on their plate", not "what did they finish".
        const rank = (t: TaskSummary) =>
          t.status === 'DONE' || t.status === 'CANCELLED' ? 1 : 0;
        setPersonTasks([...res.data].sort((a, b) => rank(a) - rank(b)));
      })
      .catch(() => setPersonTasks([]));
  }

  if (error) {
    return (
      <div className="mx-auto max-w-3xl px-8 py-16 text-center">
        <p className="text-sm text-red-600">{error}</p>
        <button type="button" onClick={() => void load()}
          className="mt-3 rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50">
          Try again
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-4 px-6 py-5">
        <div className="h-8 w-56 animate-pulse rounded bg-slate-100" />
        <div className="grid grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-20 animate-pulse rounded-xl bg-slate-100" />)}
        </div>
        <div className="h-72 animate-pulse rounded-xl bg-slate-100" />
      </div>
    );
  }

  const { tasks, period, workload } = data;
  const open = tasks.total - (tasks.byStatus.DONE ?? 0) - (tasks.byStatus.CANCELLED ?? 0);
  const maxOpen = Math.max(1, ...workload.map((w) => w.openAssigned));

  const attention = [
    { count: tasks.overdue, label: 'overdue', hint: 'past their due date', to: '/tasks?overdue=true', tone: 'text-red-600' },
    { count: tasks.unassigned, label: 'unassigned', hint: 'nobody can action these', to: '/tasks?scope=unassigned', tone: 'text-amber-700' },
    { count: tasks.byStatus.BLOCKED ?? 0, label: 'blocked', hint: 'waiting on something', to: '/tasks?status=BLOCKED', tone: 'text-red-600' },
  ].filter((a) => a.count > 0);

  // "Open" and "Overdue" both narrow by a STATUS SET (PENDING/ONGOING/
  // BLOCKED), not one exact status - the Tasks page reads these as their
  // own ?open=true / ?overdue=true params, matching the same PENDING/
  // ONGOING/BLOCKED (+ due-in-the-past, for Overdue) rule these numbers
  // are computed with server-side.
  const kpis = [
    { label: 'OPEN TASKS', value: open, sub: `of ${tasks.total} total`, tone: 'text-slate-900', to: '/tasks?open=true', kind: 'open' as const, chip: 'bg-slate-100 text-slate-600' },
    { label: 'OVERDUE', value: tasks.overdue, sub: 'need attention first', tone: tasks.overdue > 0 ? 'text-red-600' : 'text-slate-900', to: '/tasks?overdue=true', kind: 'overdue' as const, chip: 'bg-red-50 text-red-600' },
    { label: 'UNASSIGNED', value: tasks.unassigned, sub: 'open, no owner', tone: tasks.unassigned > 0 ? 'text-amber-700' : 'text-slate-900', to: '/tasks?scope=unassigned', kind: 'unassigned' as const, chip: 'bg-amber-50 text-amber-700' },
    { label: 'COMPLETED', value: tasks.byStatus.DONE ?? 0, sub: `${period.tasksCompleted} in last ${days}d`, tone: 'text-emerald-700', to: '/tasks?status=DONE', kind: 'completed' as const, chip: 'bg-emerald-50 text-emerald-700' },
  ];

  return (
    <div className="flex flex-col gap-4 px-6 py-5 xl:h-full xl:overflow-hidden">
      {/* ---- Header: title + period activity strip, one line ---- */}
      <header className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2">
        <h1 className="text-xl font-semibold text-slate-900">Dashboard</h1>
        <div className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
          <span><b className="text-slate-800">{period.tasksCreated}</b> created</span>
          <span><b className="text-emerald-700">{period.tasksCompleted}</b> completed</span>
          {/* Multi-select: every KPI, breakdown and workload row narrows
              to tasks in ANY picked department / with ANY picked person. */}
          <MultiSelect
            compact
            widthClass="w-36"
            placeholder="All departments"
            options={departments.map((d) => ({ value: d.id, label: d.name }))}
            selected={filterDepts}
            onChange={setFilterDepts}
          />
          <MultiSelect
            compact
            widthClass="w-36"
            placeholder="All people"
            options={users.map((u) => ({ value: u.id, label: u.displayName }))}
            selected={filterUsers}
            onChange={setFilterUsers}
          />
          <MultiSelect
            compact
            widthClass="w-36"
            placeholder="All projects"
            options={projects.map((p) => ({ value: p.id, label: p.name }))}
            selected={filterProjects}
            onChange={setFilterProjects}
          />
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value) as Period)}
            className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 outline-none focus:border-brand-500"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
      </header>

      {/* ---- KPI tiles ---- */}
      <div className="grid shrink-0 grid-cols-2 gap-3 xl:grid-cols-4">
        {kpis.map((kpi) => (
          <button key={kpi.label} type="button" onClick={() => navigate(kpi.to)}
            title={`Open the Tasks page filtered to ${kpi.label.toLowerCase()}`}
            className="group relative cursor-pointer rounded-xl border border-slate-200 bg-white px-4 py-3 text-left transition hover:border-brand-300 hover:shadow-sm">
            {/* The arrow says "this goes somewhere"; it nudges right on hover. */}
            <span aria-hidden="true"
              className="absolute right-3 top-2.5 text-sm text-slate-300 transition-all group-hover:translate-x-0.5 group-hover:text-brand-600">
              →
            </span>
            <span className="flex items-center gap-1.5">
              <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full ${kpi.chip}`}>
                <KpiIcon kind={kpi.kind} />
              </span>
              <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-slate-400">{kpi.label}</span>
            </span>
            <p className={`mt-1 text-2xl font-bold tabular-nums ${kpi.tone}`}>{kpi.value}</p>
            <p className="text-[11px] text-slate-500">{kpi.sub}</p>
          </button>
        ))}
      </div>

      {/* ---- Main grid: left rail (attention + status) | workload ---- */}
      <div className="grid min-h-0 flex-1 gap-4 xl:grid-cols-[minmax(260px,1fr)_2.2fr]">
        <div className="flex min-h-0 flex-col gap-4">
          {/* Needs your attention */}
          <Card className="shrink-0 p-4">
            <h2 className="text-sm font-semibold text-slate-900">Needs your attention</h2>
            {attention.length === 0 ? (
              <p className="mt-2 text-sm text-slate-500">All clear - nothing is stuck.</p>
            ) : (
              <ul className="mt-2 space-y-1.5">
                {attention.map((a) => (
                  <li key={a.label} className="flex items-center gap-2 text-sm">
                    <span className={`w-7 text-right font-bold tabular-nums ${a.tone}`}>{a.count}</span>
                    <span className="min-w-0 flex-1 truncate text-slate-600">
                      <b className="font-semibold text-slate-800">{a.label}</b> · {a.hint}
                    </span>
                    <button type="button" onClick={() => navigate(a.to)}
                      className="rounded-lg border border-slate-300 px-2 py-0.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50">
                      View
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* Tasks by status */}
          <Card className="min-h-0 flex-1 p-4">
            <h2 className="text-sm font-semibold text-slate-900">Tasks by status</h2>
            <div className="mt-3 flex h-2.5 gap-0.5 overflow-hidden rounded-full">
              {STATUS_ORDER.filter((st) => (tasks.byStatus[st] ?? 0) > 0).map((st) => (
                <span key={st} className={STATUS_DOT[st]} style={{ flex: tasks.byStatus[st] }} />
              ))}
              {tasks.total === 0 && <span className="flex-1 bg-slate-100" />}
            </div>
            <ul className="mt-3 space-y-1.5">
              {STATUS_ORDER.map((st) => (
                <li key={st} className="flex items-center gap-2 text-sm">
                  <span className={`h-2 w-2 rounded-full ${STATUS_DOT[st]}`} />
                  <span className="flex-1 text-slate-600">{TASK_STATUS_LABEL[st]}</span>
                  <span className="font-bold tabular-nums text-slate-800">{tasks.byStatus[st] ?? 0}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>

        {/* ---- Workload by person: the detail the overviewer asked for ---- */}
        <Card className="flex min-h-0 flex-col">
          <div className="flex shrink-0 items-baseline justify-between gap-3 border-b border-slate-100 px-4 py-3">
            <h2 className="text-sm font-semibold text-slate-900">
              Workload by person
              <span className="ml-2 text-xs font-normal text-slate-400">{workload.length} people</span>
            </h2>
            <span className="text-[11px] text-slate-400">tasks + sub-tasks currently assigned, by status</span>
          </div>

          {/* Column headers */}
          <div className="grid shrink-0 grid-cols-[1.7fr_repeat(5,minmax(3.2rem,0.6fr))_1.4fr] items-center gap-2 border-b border-slate-100 px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
            <span>Person</span>
            <span className="text-center" title="Top-level tasks currently assigned">Tasks</span>
            <span className="text-center" title="Sub-tasks currently assigned">Subs</span>
            <span className="text-center text-slate-500" title="Not started">● NS</span>
            <span className="text-center text-amber-600" title="In progress">● IP</span>
            <span className="text-center text-red-500" title="Blocked">● BL</span>
            <span className="pr-1 text-right">Open workload · Done</span>
          </div>

          {/* Rows - the ONLY thing that scrolls on this page. */}
          <ul className="min-h-0 flex-1 divide-y divide-slate-50 overflow-y-auto">
            {workload.map((row) => (
              <li key={row.userId}
                  role="button"
                  tabIndex={0}
                  title={`View ${row.displayName}'s tasks`}
                  onClick={() => inspect(row)}
                  onKeyDown={(e) => { if (e.key === 'Enter') inspect(row); }}
                  className="grid cursor-pointer grid-cols-[1.7fr_repeat(5,minmax(3.2rem,0.6fr))_1.4fr] items-center gap-2 px-4 py-2 transition hover:bg-slate-50">
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-slate-900">
                    {row.displayName}
                    {row.systemKey === 'SUPER_ADMIN' && <span className="ml-1.5 rounded bg-violet-100 px-1 py-0.5 text-[9px] font-bold text-violet-700">SUPER</span>}
                    {row.systemKey === 'ADMIN' && <span className="ml-1.5 rounded bg-sky-100 px-1 py-0.5 text-[9px] font-bold text-sky-700">ADMIN</span>}
                  </span>
                  <span className="block truncate text-[11px] text-slate-400">{row.roleName}</span>
                </span>
                <span className="text-center text-sm font-semibold tabular-nums text-slate-800">{row.tasksAssigned}</span>
                <span className="text-center text-sm font-semibold tabular-nums text-slate-500">{row.subtasksAssigned}</span>
                <span className="text-center text-sm tabular-nums text-slate-600">{row.notStarted}</span>
                <span className={`text-center text-sm tabular-nums ${row.inProgress > 0 ? 'font-semibold text-amber-700' : 'text-slate-400'}`}>{row.inProgress}</span>
                <span className={`text-center text-sm tabular-nums ${row.blocked > 0 ? 'font-semibold text-red-600' : 'text-slate-400'}`}>{row.blocked}</span>
                <span className="flex items-center justify-end gap-2 pr-1">
                  {/* Stacked mini-bar: the open pile split NS / IP / BL, sized against the busiest person. */}
                  <span className="flex h-1.5 w-24 gap-px overflow-hidden rounded-full bg-slate-100">
                    {row.openAssigned > 0 && (
                      <>
                        <span className="bg-slate-400" style={{ width: `${(row.notStarted / maxOpen) * 100}%` }} />
                        <span className="bg-amber-500" style={{ width: `${(row.inProgress / maxOpen) * 100}%` }} />
                        <span className="bg-red-500" style={{ width: `${(row.blocked / maxOpen) * 100}%` }} />
                      </>
                    )}
                  </span>
                  <span className="w-8 text-right text-sm font-bold tabular-nums text-slate-800">{row.openAssigned}</span>
                  <span className="w-8 text-right text-xs tabular-nums text-emerald-700">✓{row.completedAssigned}</span>
                </span>
              </li>
            ))}
          </ul>

          {tasks.unassigned > 0 && (
            <div className="flex shrink-0 items-center justify-between gap-3 border-t border-slate-100 bg-amber-50/60 px-4 py-2.5">
              <p className="text-xs text-amber-800">
                <b>{tasks.unassigned}</b> open {tasks.unassigned === 1 ? 'task has' : 'tasks have'} nobody assigned.
              </p>
              <button type="button" onClick={() => navigate('/tasks?scope=unassigned')}
                className="rounded-lg bg-amber-500 px-2.5 py-1 text-xs font-semibold text-white transition hover:bg-amber-600">
                Assign them
              </button>
            </div>
          )}
        </Card>
      </div>

      {/* ---- Drawer: one person's plate ---- */}
      {person && (
        <SidePanel
          open
          title={person.displayName}
          description={`${person.roleName} · ${person.openAssigned} open, ${person.completedAssigned} done`}
          onClose={() => { setPerson(null); setPersonTasks(null); }}
        >
          <div className="flex-1 space-y-1.5 overflow-y-auto px-5 py-4">
            {personTasks === null ? (
              [0, 1, 2].map((i) => (
                <div key={i} className="h-14 animate-pulse rounded-xl bg-slate-100" />
              ))
            ) : personTasks.length === 0 ? (
              <p className="py-10 text-center text-sm text-slate-500">
                Nothing is assigned to {person.displayName.split(/\s+/)[0]} right now.
              </p>
            ) : (
              personTasks.map((task) => {
                const tone = STATUS_TONE[task.status];
                const due = dueInfo(task);
                const closed = task.status === 'DONE' || task.status === 'CANCELLED';
                return (
                  <button
                    key={task.id}
                    type="button"
                    onClick={() => setViewing(task)}
                    className={[
                      'flex w-full items-center gap-2.5 rounded-xl border border-slate-200 border-l-[3px] bg-white px-3 py-2.5 text-left transition hover:border-slate-300 hover:shadow-sm',
                      tone.edge,
                      closed ? 'opacity-60' : '',
                    ].join(' ')}
                  >
                    <span className="min-w-0 flex-1">
                      <span className={`block truncate text-sm font-semibold ${closed ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
                        {task.title}
                      </span>
                      <span className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[11px]">
                        <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 font-semibold ring-1 ${tone.pill}`}>
                          <span className={`h-1 w-1 rounded-full ${tone.dot}`} />
                          {TASK_STATUS_LABEL[task.status]}
                        </span>
                        <span className={due.cls}>{due.text}</span>
                        <span className="font-mono text-slate-400">{formatTaskKey(task.taskKey)}</span>
                        {task.parentTaskId && <span className="text-slate-400">· sub-task</span>}
                      </span>
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </SidePanel>
      )}

      {viewing && (
        <TaskDetailPanel
          task={viewing}
          onClose={() => setViewing(null)}
          onChanged={(t) => {
            setViewing(t);
            setPersonTasks((current) =>
              current ? current.map((x) => (x.id === t.id ? t : x)) : current);
            void load();
          }}
        />
      )}
    </div>
  );
}
